// Client-side scheduled-message store + dispatcher.
//
// ping-prime has no server-side "send at" API, so scheduled messages are
// persisted locally (localStorage) and delivered by a single in-process
// ticker that fires due messages through the conversation's live send
// function. A composer registers its `onSend` as the sender for its
// conversation while mounted; the ticker delivers any due message whose
// conversation currently has a registered sender.
//
// Delivery guarantees / limits (documented, honest):
//   - Fires within `TICK_MS` (15s) of the scheduled time while the app is
//     open — the modal copy says "within a minute".
//   - Survives reload: the queue is persisted; on remount the composer
//     re-registers its sender and any overdue message fires immediately.
//   - A message only delivers while SOME composer for its conversation is
//     mounted (its sender is registered). If the user is elsewhere it
//     waits until they return to that conversation. Robust cross-session
//     scheduling needs backend support — hosts can override delivery
//     entirely via the composer's `onScheduleMessage` prop.

export interface ScheduledMessage {
  readonly id: string;
  readonly conversationId: string;
  /** Canonical markdown body to send (already slash-transformed). */
  readonly text: string;
  readonly replyTo?: string;
  /** Epoch ms at which to send. */
  readonly sendAtMs: number;
  /** Epoch ms the schedule was created (for stable ordering / display). */
  readonly createdAtMs: number;
}

export type ScheduledMessageSender = (
  text: string,
  replyTo?: string,
) => Promise<void>;

const STORAGE_KEY = "ping.composer.scheduled";
const TICK_MS = 15_000;

function storage(): Storage | null {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* sandboxed / SSR */
  }
  return null;
}

function loadAll(): ScheduledMessage[] {
  const s = storage();
  if (s === null) return [];
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (raw === null || raw.length === 0) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ScheduledMessage =>
        m !== null &&
        typeof m === "object" &&
        typeof (m as ScheduledMessage).id === "string" &&
        typeof (m as ScheduledMessage).conversationId === "string" &&
        typeof (m as ScheduledMessage).text === "string" &&
        typeof (m as ScheduledMessage).sendAtMs === "number",
    );
  } catch {
    return [];
  }
}

function saveAll(list: ScheduledMessage[]): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* quota / disabled — best-effort */
  }
}

// In-memory mirror so reads are cheap and multiple tabs don't clobber a
// tick mid-flight. Seeded from storage on first access.
let cache: ScheduledMessage[] | null = null;
function all(): ScheduledMessage[] {
  if (cache === null) cache = loadAll();
  return cache;
}
function commit(next: ScheduledMessage[]): void {
  cache = next;
  saveAll(next);
  for (const l of listeners) l();
}

const listeners = new Set<() => void>();
const senders = new Map<string, ScheduledMessageSender>();
let idCounter = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let ticking = false;

/** Subscribe to any change in the queue. Returns an unsubscribe fn. */
export function subscribeScheduled(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Pending messages for a conversation, soonest-first. */
export function listScheduledMessages(
  conversationId: string,
): ScheduledMessage[] {
  return all()
    .filter((m) => m.conversationId === conversationId)
    .sort((a, b) => a.sendAtMs - b.sendAtMs);
}

/** Queue a message for later delivery. */
export function scheduleMessage(input: {
  conversationId: string;
  text: string;
  replyTo?: string;
  sendAtMs: number;
}): ScheduledMessage {
  const now = Date.now();
  const msg: ScheduledMessage = {
    id: `sched_${now}_${idCounter++}`,
    conversationId: input.conversationId,
    text: input.text,
    ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
    sendAtMs: input.sendAtMs,
    createdAtMs: now,
  };
  commit([...all(), msg]);
  ensureTicker();
  // Fire a check on the next tick in case it's already due.
  void runDueMessages();
  return msg;
}

/** Remove a queued message (user cancelled). */
export function cancelScheduledMessage(id: string): void {
  const next = all().filter((m) => m.id !== id);
  if (next.length !== all().length) commit(next);
}

/**
 * Register the send function for a conversation while its composer is
 * mounted. Returns an unregister fn. Registering immediately checks for
 * overdue messages so a reload with a past-due item fires at once.
 */
export function registerScheduledSender(
  conversationId: string,
  sender: ScheduledMessageSender,
): () => void {
  senders.set(conversationId, sender);
  ensureTicker();
  void runDueMessages();
  return () => {
    if (senders.get(conversationId) === sender) {
      senders.delete(conversationId);
    }
  };
}

function ensureTicker(): void {
  if (timer !== null || typeof setInterval === "undefined") return;
  timer = setInterval(() => {
    void runDueMessages();
  }, TICK_MS);
}

async function runDueMessages(): Promise<void> {
  if (ticking) return;
  ticking = true;
  try {
    const now = Date.now();
    // Snapshot due messages that have a live sender; deliver oldest-first.
    const due = all()
      .filter((m) => m.sendAtMs <= now && senders.has(m.conversationId))
      .sort((a, b) => a.sendAtMs - b.sendAtMs);
    for (const msg of due) {
      const sender = senders.get(msg.conversationId);
      if (sender === undefined) continue;
      try {
        await sender(msg.text, msg.replyTo);
        commit(all().filter((m) => m.id !== msg.id));
      } catch {
        // Leave it queued; the next tick retries. (A permanently failing
        // send would retry every TICK_MS — acceptable for a local queue.)
      }
    }
  } finally {
    ticking = false;
  }
}

// Test-only reset hook (no-op cost in prod). Clears in-memory state so
// unit tests don't leak the ticker / cache across cases.
export function __resetScheduledForTests(): void {
  cache = null;
  senders.clear();
  listeners.clear();
  idCounter = 0;
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  ticking = false;
}
